export interface ExpertFaqItem {
  id: string;
  question: string;
  answer: string;
}

// Single source of truth for the expert FAQ: rendered visibly by
// <ExpertsFAQ /> and consumed as JSON-LD by <FAQJsonLd />. Google's FAQ
// rich-result policy requires the structured data to match what users see.
export const expertFaqs: ExpertFaqItem[] = [
  {
    id: "what-is-terac",
    question: "What is Terac?",
    answer:
      "Terac is an expert marketplace. We connect verified domain experts to paid work from market research companies and human data companies (including frontier AI labs). Experts are sourced once, vetted thoroughly, and matched to engagements across both sides of the marketplace.",
  },
  {
    id: "what-is-the-work",
    question: "What is the work?",
    answer:
      "Remote, paid work grounded in your professional background. Engagements come from two types of buyers. Market research companies pay you to share your expertise in interviews, surveys, and product feedback. Human data companies and AI labs pay you to review AI outputs in your domain, set the bar for what expert-level work looks like, and tackle specialized cases that need real reps.",
  },
  {
    id: "application-process",
    question: "What does the application process look like?",
    answer:
      "Share your background and complete a short AI assessment. If it is a fit, our team follows up and matches you to projects aligned with your skills. No prior AI experience required.",
  },
  {
    id: "ai-interview",
    question: "What is the AI interview?",
    answer:
      "A short conversational screen that adapts to your background and asks questions relevant to your field so we can accurately assess your expertise. Your responses are not sold and are not used to train customer AI models.",
  },
  {
    id: "pay",
    question: "How much do I get paid?",
    answer:
      "Compensation depends on your expertise, the complexity of the project, and demand for the role. Projects are clearly scoped and payment terms are defined upfront. High-performing experts are invited to ongoing opportunities.",
  },
  {
    id: "qualifications",
    question: "What qualifications do I need?",
    answer:
      "We work with professionals across a wide range of domains. Minimum qualifications typically require undergraduate-level expertise. Many projects require years of professional work experience or an advanced degree in the relevant domain.",
  },
  {
    id: "duration",
    question: "How long do projects last?",
    answer:
      "Project length varies. The range is weeks to months. Projects can be extended, shortened, or concluded early depending on need and performance.",
  },
  {
    id: "referral",
    question: "Can I refer friends or colleagues?",
    answer:
      "Yes. You can activate a referral link from a job listing and share it directly. If your referral is approved and begins working on a project, you may be eligible for a referral bonus.",
  },
];

export const faqs = [
  {
    question: "What is Terac?",
    answer:
      "Terac is an expert marketplace. We aggregate, verify, and operate a global panel of vetted domain experts, then sell on-demand access to that supply through our platform and API. Our customers are market research companies and human data companies who need verified humans to power their products.",
  },
  {
    question: "Who buys from Terac?",
    answer:
      "Two types of customers. Market research companies like Maze, Listen Labs, and Tegus use Terac to source niche, verified audiences for their studies. Human data companies and frontier AI labs use Terac to source domain experts for RL post-training, evals, and red-teaming. Both groups buy access to the same verified expert supply.",
  },
  {
    question: "How is Terac different from a traditional panel provider?",
    answer:
      "Traditional panels rent third-party networks and screen with checkbox surveys. Terac owns and operates its own panel, screens every expert with conversational AI that verifies identity, credentials, and domain knowledge, and exposes the supply programmatically. You buy verified humans, not survey responses.",
  },
  {
    question: "How do you ensure expert quality?",
    answer:
      "Every expert clears our screening pipeline: identity attestation, credential verification, an open-ended AI conversation that probes domain knowledge, behavioral analysis, and ongoing performance scoring. Profiles get richer with every engagement, so quality compounds the longer an expert is on the platform.",
  },
  {
    question: "How does pricing work?",
    answer:
      "Pay-as-you-go: you cover the expert's payout plus our platform fee. For market research companies, human data companies, and other buyers with high or recurring volume, we offer custom enterprise packages with negotiated rates and dedicated supply.",
  },
];
